import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const loadProfile = async (userId) => {
    if (!userId) return;
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Profile load error:', error);
        return;
      }

      if (!data) {
        setUserProfile(null);
        return;
      }

      if (data?.current_subscription_id) {
        const { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('id', data.current_subscription_id)
          .maybeSingle();

        if (!subError && subData) {
          setUserProfile({ ...data, subscription: subData });
          return;
        }
      }

      setUserProfile(data);
    } catch (e) {
      console.error('Profile load error:', e);
    } finally {
      setProfileLoading(false);
    }
  };

  // ✅ auth callback strictement sync
  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // ✅ fetch profile séparé (quand user change)
  useEffect(() => {
    if (!user?.id) {
      setUserProfile(null);
      setProfileLoading(false);
      return;
    }
    loadProfile(user.id);
  }, [user?.id]);

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      return { data, error };
    } catch {
      return { error: { message: 'Erreur réseau. Veuillez réessayer.' } };
    }
  };

  // ✅ OPTION B ROBUSTE: si session pas encore dispo, on l'attend brièvement
  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata }
      });

      if (error) return { data, error };

      // Si la session est déjà là (email confirmation OFF), parfait
      if (data?.session?.user) {
        return { data, error: null };
      }

      // Sinon on attend l'event auth (max ~2s) pour éviter redirect trop tôt
      const waited = await new Promise((resolve) => {
        let done = false;

        const timeout = setTimeout(async () => {
          if (done) return;
          done = true;
          const { data: s } = await supabase.auth.getSession();
          resolve({ session: s.session ?? null });
        }, 2000);

        const { data: listener } = supabase.auth.onAuthStateChange((_evt, session) => {
          if (done) return;
          if (session?.user) {
            done = true;
            clearTimeout(timeout);
            listener?.subscription?.unsubscribe();
            resolve({ session });
          }
        });
      });

      return { data: { ...data, session: waited?.session ?? data?.session }, error: null };
    } catch {
      return { error: { message: 'Erreur réseau. Veuillez réessayer.' } };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (!error) {
        setUser(null);
        setUserProfile(null);
        setProfileLoading(false);
      }
      return { error };
    } catch {
      return { error: { message: 'Erreur réseau. Veuillez réessayer.' } };
    }
  };

  const updateProfile = async (updates) => {
    if (!user?.id) return { error: { message: 'Aucun utilisateur connecté' } };
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .maybeSingle();

      if (!error && data) {
        setUserProfile(data);
      }
      return { data, error };
    } catch {
      return { error: { message: 'Erreur réseau. Veuillez réessayer.' } };
    }
  };

  const refreshProfile = async () => {
    if (!user?.id) return;
    await loadProfile(user.id);
  };

  const value = useMemo(() => ({
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user
  }), [user, userProfile, loading, profileLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
